import React, { useEffect, useState } from 'react';

// Imports from the old MeetingHubTabsModal / related hooks & components
import { ParticipantsFilter } from '@lib/core/src/components/ParticipantsFilter';
import SearchInput from '@lib/core/src/components/SearchInput';
import palette from '@lib/core/src/theme/palette';
// MUI Imports
import { Box, Pagination, Stack, Tab, Tabs, Typography, styled } from '@mui/material';
import dayjs from 'dayjs';

import { useGetCalendarMeeting } from '@/api/generated';
import ChatContainer from '@/components/AskMeAnything/ChatContainer';
import { DateRangeFilter } from '@/components/MeetingHub/DateRangeFilter';
import { MeetingHubContent } from '@/components/MeetingHub/MeetingHubContent';
import { MeetingHubTabs } from '@/enums/MeetingHubTabs';
import { useMeetingsHub } from '@/hooks/useMeetingsHub';
import PageContainer from '@/pages/PageContainer';

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 'auto',
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: palette.black
  }
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 'auto',
  padding: theme.spacing(1.2, 0),
  marginRight: theme.spacing(4),
  textTransform: 'none',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: palette.black,
    fontWeight: 'bold'
  }
}));

const MeetingHubPage: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState<MeetingHubTabs>(MeetingHubTabs.All);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    handleParticipantClick,
    setParticipantSearchValue,
    setSelectedParticipants,
    setSelectedDateRange,
    initFilters,
    participantSearchValue,
    participants,
    selectedDateRange,
    isLoading,
    groupedMeetings,
    isEmptyGroupedMeetings,
    selectedParticipants
  } = useMeetingsHub({
    searchMeetingValue: searchValue,
    tabValue: activeTab,
    setSearchValue
  });

  const [meetingHoverId, setMeetingHoverId] = useState<string>('');

  const { data: meetingResponse, isLoading: meetingLoading } = useGetCalendarMeeting(
    meetingHoverId,
    {
      query: {
        queryKey: ['getCalendarMeeting', meetingHoverId],
        enabled: !!meetingHoverId,
        staleTime: 5 * 60 * 1000
      }
    }
  );

  const summary = meetingResponse?.items?.at(0)?.overview?.brief;

  const handleTabChange = (event: React.SyntheticEvent, newValue: MeetingHubTabs) => {
    setActiveTab(newValue);
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const onDateChange = (date: [Date | null, Date | null]) => {
    const [start, end] = date;

    const normalizedStart = start ? dayjs(start).startOf('day').valueOf() : undefined;
    const normalizedEnd = end ? dayjs(end).endOf('day').valueOf() : undefined;

    setSelectedDateRange({
      startDate: normalizedStart,
      endDate: normalizedEnd
    });

    setCurrentPage(1);
  };

  useEffect(() => {
    if (groupedMeetings.length > 0 && currentPage > groupedMeetings.length) {
      setCurrentPage(groupedMeetings.length);
    } else if (groupedMeetings.length === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [groupedMeetings, currentPage]);

  return (
    <PageContainer backRoute="/">
      {/* Header: Title and Search */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontSize: '28px', color: palette.black, fontWeight: 700 }}>
          Meetings Hub
        </Typography>
        <SearchInput
          backgroundColor={palette.white}
          value={searchValue}
          onChange={value => setSearchValue(value)}
          placeholder="Search a meeting..."
        />
      </Stack>

      {/* Tabs */}
      <Box>
        <StyledTabs
          sx={{
            '& .MuiTab-root': { color: palette.grey },
            '& .MuiTab-root.Mui-selected': { color: palette.black, fontWeight: 'bold' }
          }}
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Meeting hub tabs"
        >
          <StyledTab
            sx={{
              borderBottom:
                activeTab === MeetingHubTabs.All ? `2px solid ${palette.grey[100]}` : 'none',
              textTransform: 'none'
            }}
            label="All Meetings"
            value={MeetingHubTabs.All}
          />
          <StyledTab
            sx={{
              borderBottom:
                activeTab === MeetingHubTabs.All ? `2px solid ${palette.grey[100]}` : 'none',
              textTransform: 'none'
            }}
            label="Shared with Me"
            value={MeetingHubTabs.Shared}
          />
        </StyledTabs>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        {/* Filters */}
        <Stack direction="row" spacing={1}>
          <DateRangeFilter dateRange={selectedDateRange} onDateChange={onDateChange} />
          <ParticipantsFilter
            participants={participants}
            selectedCount={selectedParticipants.length}
            onParticipantSelect={handleParticipantClick}
            onSearch={setParticipantSearchValue}
            searchValue={participantSearchValue}
            clearFilter={() => {
              setSelectedParticipants([]);
              setCurrentPage(1);
            }}
          />
        </Stack>

        {/* Pagination */}
        {groupedMeetings.length > 0 && (
          <Pagination
            count={groupedMeetings.length}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: '50%',
                margin: '0 2px'
              },
              '& button.Mui-selected': {
                backgroundColor: palette.black,
                color: palette.white,
                '&:hover': {
                  backgroundColor: palette.black,
                  color: palette.white
                }
              },
              '& button:not(.Mui-selected)': {
                color: palette.black,
                border: `1px solid ${palette.grey[300]}`,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: palette.blue
                }
              }
            }}
          />
        )}
      </Stack>

      {/* Content Area */}
      <Box sx={{ mt: 2, overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
        <MeetingHubContent
          dayGroups={groupedMeetings[currentPage - 1]?.dayGroups ?? []}
          isLoading={isLoading}
          isEmptyGroupedMeetings={isEmptyGroupedMeetings && !isLoading}
          initFilters={initFilters}
          summary={summary || ''}
          setMeetingHoverId={setMeetingHoverId}
          meetingHoverId={meetingHoverId}
          meetingLoading={meetingLoading}
        />
      </Box>
      <ChatContainer />
    </PageContainer>
  );
};

export default MeetingHubPage;
